import styled from '@emotion/styled';
import { alpha, Box, useTheme } from '@material-ui/core';
import React, { useEffect, useRef, useState } from 'react';
import {
  Menu,
  menuClasses,
  MenuItem,
  MenuItemStyles,
  Sidebar,
} from 'react-pro-sidebar';
import { Link, useLocation } from 'react-router-dom';
// import useAuth from 'src/hooks/useAuth';
import { useAppDispatch } from 'src/hooks/useRedux';
import ReportingIcon from 'src/icons/Reporting';
import ProjectsIcon from 'src/icons/Projects';
import CasesIcon from 'src/icons/Cases';
import Users from 'src/icons/Users';
import DashboardIcon from 'src/icons/Dashboard';

import {
  getAllFavouriteProjects,
  getAllProjects,
} from 'src/store/slices/projectsSlice';
import { getAllRoles } from 'src/store/slices/rolesSlice';
import useHasPermissions from 'src/hooks/useHasPermission';
import {
  CLIENT_CASES,
  CLIENT_PROJECTS,
  CLIENT_USERS,
  REPORTING,
} from 'src/constants/constants';
import PlanningIcon from 'src/icons/PlanningIcon';
import { ChevronRight } from '@material-ui/icons';
import { CollapseIcon } from 'src/icons/CollapseIcon';

const StyledSidebar = styled(Sidebar)({
  position: 'relative',
  '& .activeTabIndicator': {
    display: 'none'
  },
  '&.ps-collapsed .ps-menu-label': {
    display: 'none !important',
  },

  '&.ps-collapsed .ps-menu-button': {
    justifyContent: 'center !important',
    paddingLeft: '0 !important',
  },
  '&.ps-collapsed .ps-menu-icon': {
    marginRight: '0 !important',
  },

  '& .ps-sidebar-container': {
    overflowX: 'hidden',
  },
});

const DashboardSidebar: React.FC = () => {
  const checkPermissions = useHasPermissions();
  const theme = useTheme();
  const [collapsed, setCollapsed] = React.useState(false);
  const [toggled, setToggled] = React.useState(false);

  // const { logout } = useAuth();
  const location = useLocation();
  const { pathname } = location;
  const itemRefs = useRef([]);
  const [indicatorStyle, setIndicatorStyle] = useState({ top: 0 });
  const dispatch = useAppDispatch();

  const getActiveTabIndex = () => {
    switch (true) {
      case pathname.includes('/cases/dashboard'):
        return 0;
      case pathname.includes('/cases'):
        return 1;
      case pathname.includes('/projects'):
        return 2;
      case pathname.includes('/reporting'):
        return 3;
      case pathname.includes('/planning'):
        return 4;
      case pathname.includes('/contacts'):
        return 5;
      default:
        return null;
    }
  };

  const [activeTabIndex, setActiveTabIndex] = useState(getActiveTabIndex());

  const menuItemStyles: MenuItemStyles = {
    root: {
      fontSize: '14px',
      fontFamily: 'Inter',
      fontWeight: 600,
      // marginTop: '12px',
      '& .activeTabIndicator': {
        display: 'none'
      },
      [`&.${menuClasses.menuItemRoot}`]: {
        marginBottom: '6px',
        color: '#425466',
        // padding: '0',
        '& a': {
          height: '50px',
          padding: '4px 4px',
          margin: '0 12px 0 8px',
          borderRadius: '8px',
          '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.15),
            borderRadius: '8px',
          },
        },
      },
    },
    icon: {},
    SubMenuExpandIcon: {},
    // inside menuItemStyles
    button: {
      position: 'relative',
      '&:hover': {
        backgroundColor: `${alpha(theme.palette.primary.main, 0.15)} !important`,
        borderRadius: '8px',
        margin: '0 12px 0 8px',
      },
      [`&.${menuClasses.active}`]: {
        transition: 'all 0.5s ease',
        color: `${theme.palette.primary.main} !important`,
        backgroundColor: `${alpha(theme.palette.primary.main, 0.15)} !important`,
        borderRadius: '8px',
        margin: '0 12px 0 8px',
      },
    },
  };

  const menuIconClick = () =>
    collapsed ? setCollapsed(false) : setCollapsed(true);

  useEffect(() => {
    if (itemRefs.current[activeTabIndex]) {
      setIndicatorStyle({ top: itemRefs.current[activeTabIndex].offsetTop });
    }
  }, [activeTabIndex]);

  useEffect(() => {
    setIndicatorStyle({ top: itemRefs.current[getActiveTabIndex()]?.offsetTop });
    setActiveTabIndex(getActiveTabIndex());
  }, [pathname]);

  useEffect(() => {
    dispatch(getAllProjects());
    dispatch(getAllFavouriteProjects());
    dispatch(getAllRoles());
  }, []);

  const isCasesRoute = pathname.includes('/cases');

  const menuItems = React.useMemo(() => (
    <Menu menuItemStyles={menuItemStyles}>
      <MenuItem
        style={{ marginTop: '12px' }}
        component={<Link to="/cases/dashboard" />}
        onClick={() => setActiveTabIndex(0)}
        ref={(el) => (itemRefs.current[0] = el)}
        icon={
          <DashboardIcon
            color={
              activeTabIndex === 0
                ? theme.palette.primary.main
                : undefined
            }
          />
        }
        active={activeTabIndex === 0}>
        Dashboard
      </MenuItem>

      {checkPermissions([CLIENT_CASES]) && (
        <MenuItem
          component={<Link to="/cases/all" />}
          onClick={() => setActiveTabIndex(1)}
          ref={(el) => (itemRefs.current[1] = el)}
          icon={
            <CasesIcon
              color={
                isCasesRoute && activeTabIndex !== 0
                  ? theme.palette.primary.main
                  : undefined
              }
            />
          }
          active={isCasesRoute && activeTabIndex !== 0}>
          Cases
        </MenuItem>
      )}

      {checkPermissions([CLIENT_PROJECTS]) && (
        <MenuItem
          component={<Link to="/projects" />}
          onClick={() => setActiveTabIndex(2)}
          ref={(el) => (itemRefs.current[2] = el)}
          icon={
            <ProjectsIcon
              color={
                activeTabIndex === 2
                  ? theme.palette.primary.main
                  : undefined
              }
            />
          }
          active={activeTabIndex === 2}>
          Projects
        </MenuItem>
      )}

      {checkPermissions([REPORTING]) && (
        <MenuItem
          component={<Link to="/reporting" />}
          onClick={() => setActiveTabIndex(3)}
          ref={(el) => (itemRefs.current[3] = el)}
          icon={
            <ReportingIcon
              color={
                activeTabIndex === 3
                  ? theme.palette.primary.main
                  : undefined
              }
            />
          }
          active={activeTabIndex === 3}>
          Reporting
        </MenuItem>
      )}

      {checkPermissions([REPORTING]) && (
        <MenuItem
          component={<Link to="/planning" />}
          onClick={() => setActiveTabIndex(4)}
          ref={(el) => (itemRefs.current[4] = el)}
          icon={
            <PlanningIcon
              color={
                activeTabIndex === 4
                  ? theme.palette.primary.main
                  : undefined
              }
            />
          }
          active={activeTabIndex === 4}>
          Planning
        </MenuItem>
      )}

      {checkPermissions([CLIENT_USERS]) && (
        <MenuItem
          component={<Link to="/contacts" />}
          ref={(el) => (itemRefs.current[5] = el)}
          onClick={() => setActiveTabIndex(5)}
          icon={
            <Users
              color={
                activeTabIndex === 5
                  ? theme.palette.primary.main
                  : undefined
              }
            />
          }
          active={activeTabIndex === 5}>
          Users
        </MenuItem>
      )}
    </Menu>
  ), [activeTabIndex, theme.palette.primary.main, checkPermissions, isCasesRoute]);

  return (
    <Box
      sx={{
        display: 'flex',
        height: '100%',
        position: 'relative',
      }}>
      <StyledSidebar
        collapsed={collapsed}
        backgroundColor="#FFFFFF"
        transitionDuration={600}
        toggled={toggled}
        onBackdropClick={() => setToggled(false)}
        width="220px"
        breakPoint="md"
      >
        <div
          style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <div style={{ flex: 1, marginBottom: '32px' }}>
            {menuItems}
          </div>
          <Menu menuItemStyles={menuItemStyles} />

        </div>

        <div className="activeTabIndicator" style={indicatorStyle} />
        <Box
          role="button"
          tabIndex={0}
          sx={{
            position: 'absolute',
            bottom: '20px',
            left: collapsed ? '50%' : '8px',
            right: collapsed ? 'auto' : '8px',
            transform: collapsed ? 'translateX(-50%)' : 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: collapsed ? 'center' : 'space-between',
            color: '#425466',
            cursor: 'pointer',
            padding: '16px 12px',
            fontSize: '14px',
            fontWeight: 600,
            fontFamily: 'Inter',
            borderTop: '1px solid',
            borderColor: '#EDF2F7',
            margin: '0 4px',
            '&:hover': {
              backgroundColor: `${alpha(theme.palette.primary.main, 0.15)}`,
              borderRadius: '8px',
            },
          }}
          onClick={menuIconClick}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              menuIconClick();
            }
          }}>
          {!collapsed && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '20px', }}>
              <CollapseIcon />
              <span>Collapse</span>
            </div>
          )}
          <ChevronRight
            sx={{
              fontSize: '22px',
              color: '#A0AEC0',
              transition: 'transform ease 0.6s',
              transform: collapsed ? 'rotate(0deg)' : 'rotate(180deg)',
            }}
          />
        </Box>
      </StyledSidebar>
    </Box>
  );
};

export default DashboardSidebar;